import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINES = ["Under 1 week", "1–2 weeks", "2–4 weeks", "Flexible / Not sure"];

export default function QuoteForm({ compact = false }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", service: "", quantity: "", timeline: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone || !f.service || !f.details) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, f);
      setDone(true);
      toast.success("Quote request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="rounded-3xl bg-white border border-black/5 p-10 md:p-14 text-center max-w-[720px] mx-auto shadow-[0_30px_60px_-30px_rgba(0,0,0,0.15)]"
        data-testid="quote-success"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-[color:var(--accent)]/10 grid place-items-center text-[color:var(--accent)]">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="font-display text-4xl md:text-5xl mt-6 tracking-tight">Thank you.</h3>
        <p className="mt-4 text-[color:var(--text-2)] text-[16px] leading-[1.65] max-w-[520px] mx-auto">
          Your quote request has been received. A confirmation email is on its way, and a member of our team will reply shortly with pricing and timeline.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 font-mono text-[12px] tracking-[0.18em] uppercase">
          <a href={SITE.phoneHref} className="link-underline">{SITE.phone}</a>
          <span className="text-[color:var(--text-2)]">·</span>
          <a href={SITE.emailHref} className="link-underline">Email Us</a>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" data-testid="quote-form">
      <Field label="Full name *" name="name" value={f.name} onChange={set("name")} />
      <Field label="Email *" name="email" type="email" value={f.email} onChange={set("email")} />
      <Field label="Phone *" name="phone" value={f.phone} onChange={set("phone")} />
      <Select label="Service *" name="service" value={f.service} onChange={set("service")}>
        <option value="">Choose a service…</option>
        {SERVICES.map(s => <option key={s.slug} value={s.title}>{s.title}</option>)}
        <option value="Other">Other / Not listed</option>
      </Select>
      <Field label="Quantity (optional)" name="quantity" value={f.quantity} onChange={set("quantity")} placeholder="e.g. 500 cards, 200 booklets" />
      <Select label="Timeline (optional)" name="timeline" value={f.timeline} onChange={set("timeline")}>
        <option value="">Select a timeline…</option>
        {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
      </Select>
      <div className="md:col-span-2">
        <Textarea label="Project details *" name="details" value={f.details} onChange={set("details")} rows={5} placeholder="Sizes, stocks, finishes, delivery needs, or a description of your project." />
      </div>
      <div className="md:col-span-2 mt-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-[color:var(--text-2)] max-w-[440px]">
          By submitting, you agree to be contacted about your project. We never share your details.
        </p>
        <button
          type="submit"
          disabled={loading}
          className="btn-accent inline-flex items-center gap-3 rounded-full pl-7 pr-6 py-4 text-[12.5px] tracking-[0.18em] uppercase font-medium disabled:opacity-70"
          data-testid="quote-submit"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Submit Request <ArrowUpRight className="w-4 h-4" /></>}
        </button>
      </div>
    </form>
  );
}

function inputCls() {
  return "w-full bg-white border border-black/10 rounded-xl px-4 py-3.5 text-[15px] text-[color:var(--text)] placeholder:text-[color:var(--text-2)]/60 focus:border-[color:var(--text)] focus:outline-none transition-colors";
}

function Field({ label, name, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--text-2)] block mb-2">{label}</span>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={inputCls()}
        data-testid={`quote-field-${name}`}
      />
    </label>
  );
}

function Select({ label, name, value, onChange, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--text-2)] block mb-2">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={inputCls()}
        data-testid={`quote-field-${name}`}
      >
        {children}
      </select>
    </label>
  );
}

function Textarea({ label, name, value, onChange, rows = 4, placeholder }) {
  return (
    <label className="block">
      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--text-2)] block mb-2">{label}</span>
      <textarea
        name={name}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={inputCls() + " resize-none"}
        data-testid={`quote-field-${name}`}
      />
    </label>
  );
}
